import { CalendarIcon } from '@/../public/assets/icons';
import { manageProfileStyles } from '@/components/layouts/landing/ManageProfile/manageProfile.styles';
import BarLoader from '@/components/ui/loaders/BarLoader';
import { getDiscountById } from '@/services/discount/discount.service';
import {
   Alert,
   Box,
   Button,
   Dialog,
   Field,
   HStack,
   Input,
   Portal,
   RadioGroup,
   Stack,
   Textarea,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { usePathname } from 'next/navigation';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { RiDiscountPercentFill } from 'react-icons/ri';
import {
   createDiscountPackageValidator,
   TPackageDiscount,
   updatePackageDiscountValidator,
} from './packageDiscount.validator';

type Props = {
   isOpen: boolean;
   onClose: () => void;
   id?: string;
   setDiscounts: Dispatch<SetStateAction<TPackageDiscount[]>>;
};

const DiscountModalPackage = ({ isOpen, onClose, id, setDiscounts }: Props) => {
   const pathname = usePathname();
   const dateInputRef = useRef<HTMLInputElement | null>(null);
   const openDatePicker = () => {
      if (dateInputRef.current) {
         dateInputRef.current.showPicker();
      }
   };
   const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting },
      setError,
      reset,
      setValue,
      watch,
   } = useForm<TPackageDiscount>({
      resolver: zodResolver(id ? updatePackageDiscountValidator : createDiscountPackageValidator),
      defaultValues: {
         amount_type: 'percentage',
      },
   });

   const amountTypeItems = [
      { label: 'Percentage', value: 'percentage' },
      { label: 'Fixed', value: 'fixed' },
   ];

   const amountType = watch('amount_type');

   const onCreateSubmit = handleSubmit(async value => {
      const payload = {
         amount: value.amount,
         code: value.code,
         amount_type: value.amount_type,
         usage_type: 'single-use',
         start_date: new Date().toISOString(),
         expiry_date: value.expiry_date,
         userType: pathname.includes('/partner') ? 'PARTNER' : 'CLINIC',
         title: value.title,
         description: value.description,
      };

      setDiscounts(prev => [...prev, payload]);
      onClose();
   });

   const fetchDiscount = useCallback(
      async (id: string) => {
         const [error, data] = await getDiscountById(id);

         if (error) {
            setError('root', { message: error.response?.data.message as string, type: 'manual' });
            return console.log(error);
         }
         reset({
            code: data.response.code,
            amount: data.response.amount,
            expiry_date: dayjs(data.response.expiry_date).format('YYYY-MM-DD'),
            title: data.response.meta?.title ?? '',
            description: data.response.meta?.description ?? '',
         });
      },
      [reset, setError],
   );

   useEffect(() => {
      if (id) fetchDiscount(id);
   }, [id, fetchDiscount]);

   const onSubmit = onCreateSubmit;

   return (
      <HStack>
         {!!isSubmitting && <BarLoader loadingText="Submitting" />}
         <Dialog.Root placement="center" motionPreset="slide-in-bottom" open={isOpen} size="sm">
            <Portal>
               <Dialog.Backdrop />
               <Dialog.Positioner>
                  <Dialog.Content bg="#202020" rounded="15px" py="4" px="5" maxWidth="500px">
                     <form onSubmit={onSubmit}>
                        <Dialog.Body>
                           <Box
                              w="16"
                              h="16"
                              rounded="50%"
                              m="0 auto 10px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                           >
                              <RiDiscountPercentFill color="#f5e876" size={36} />
                           </Box>
                           <Dialog.Title color="white" textStyle="4xl" textAlign="center" mb="2">
                              {id ? 'Update ' : 'Create '} Discount
                           </Dialog.Title>

                           {errors.root?.message && (
                              <Alert.Root status="error" my={4}>
                                 <Alert.Indicator />
                                 <Alert.Title>{errors.root.message}</Alert.Title>
                              </Alert.Root>
                           )}
                           <Stack>
                              <Box css={manageProfileStyles.double_input}>
                                 <Field.Root invalid={!!errors.title}>
                                    <Field.Label textStyle="xs" color="white" mb="1">
                                       Title
                                       <Box as="span" color="error">
                                          *
                                       </Box>
                                    </Field.Label>
                                    <Input
                                       placeholder="Title for your discount offer"
                                       variant="subtle"
                                       h="10"
                                       rounded="12px"
                                       bg="#414141"
                                       color="#ffffff"
                                       _placeholder={{ color: '#7A7A7A' }}
                                       {...register('title')}
                                    />
                                    {errors.title && <Field.ErrorText>{errors.title.message}</Field.ErrorText>}
                                 </Field.Root>
                              </Box>
                              <Box css={manageProfileStyles.double_input}>
                                 <Field.Root invalid={!!errors.description}>
                                    <Field.Label textStyle="xs" color="white" mb="1">
                                       Description
                                       <Box as="span" color="error">
                                          *
                                       </Box>
                                    </Field.Label>
                                    <Textarea
                                       placeholder="Description for your discount offer"
                                       variant="subtle"
                                       h="20"
                                       rounded="12px"
                                       bg="#414141"
                                       color="#ffffff"
                                       _placeholder={{ color: '#7A7A7A' }}
                                       {...register('description')}
                                       rows={4}
                                    />
                                    {errors.description && (
                                       <Field.ErrorText>{errors.description.message}</Field.ErrorText>
                                    )}
                                 </Field.Root>
                              </Box>
                              <Box css={manageProfileStyles.double_input}>
                                 <Field.Root invalid={!!errors.code}>
                                    <Field.Label textStyle="xs" color="white" mb="1">
                                       Discount Code
                                       <Box as="span" color="error">
                                          *
                                       </Box>
                                    </Field.Label>
                                    <Input
                                       placeholder="Discount Code"
                                       variant="subtle"
                                       h="10"
                                       rounded="12px"
                                       bg="#414141"
                                       color="#ffffff"
                                       _placeholder={{ color: '#7A7A7A' }}
                                       {...register('code')}
                                    />
                                    {errors.code && <Field.ErrorText>{errors.code.message}</Field.ErrorText>}
                                 </Field.Root>
                              </Box>
                              <Box css={manageProfileStyles.double_input}>
                                 <Field.Root invalid={!!errors.expiry_date}>
                                    <Field.Label textStyle="xs" color="white" mb="1">
                                       Expiry Date
                                       <Box as="span" color="error">
                                          *
                                       </Box>
                                    </Field.Label>
                                    <Box position="relative" w="full" onClick={openDatePicker}>
                                       <Box css={manageProfileStyles.icon_box}>
                                          <CalendarIcon />
                                       </Box>
                                       <Input
                                          css={manageProfileStyles.date_input}
                                          type="date"
                                          placeholder="Date Of Birth"
                                          variant="subtle"
                                          h="10"
                                          rounded="12px"
                                          bg="#414141"
                                          color="#fff"
                                          {...register('expiry_date')}
                                          ref={e => {
                                             register('expiry_date').ref(e);
                                             dateInputRef.current = e;
                                          }}
                                       />
                                    </Box>
                                    {errors.expiry_date && (
                                       <Field.ErrorText>{errors.expiry_date.message}</Field.ErrorText>
                                    )}
                                 </Field.Root>
                              </Box>

                              <Field.Root>
                                 <Field.Label textStyle="xs" color="white" mb="1">
                                    Amount type
                                 </Field.Label>
                                 <RadioGroup.Root
                                    name="amount_type"
                                    onChange={undefined}
                                    onValueChange={({ value }) => setValue('amount_type', value!)}
                                    defaultValue="percentage"
                                 >
                                    <HStack gap="6">
                                       {amountTypeItems.map(item => (
                                          <RadioGroup.Item key={item.value} value={item.value}>
                                             <RadioGroup.ItemHiddenInput />
                                             <RadioGroup.ItemIndicator />
                                             <RadioGroup.ItemText style={{ color: '#ffffffff' }}>
                                                {item.label}
                                             </RadioGroup.ItemText>
                                          </RadioGroup.Item>
                                       ))}
                                    </HStack>
                                 </RadioGroup.Root>
                              </Field.Root>

                              <Field.Root invalid={!!errors.amount}>
                                 <Field.Label textStyle="xs" color="white" mb="1">
                                    Discount {amountType === 'fixed' ? 'Fixed' : 'Percentage'}
                                    <Box as="span" color="error">
                                       *
                                    </Box>
                                 </Field.Label>
                                 <Input
                                    placeholder={`Discount ${amountType === 'fixed' ? 'Fixed' : 'Percentage'}`}
                                    variant="subtle"
                                    h="10"
                                    type="number"
                                    rounded="12px"
                                    bg="#414141"
                                    color="#ffffff"
                                    _placeholder={{ color: '#7A7A7A' }}
                                    {...register('amount', { valueAsNumber: true })}
                                 />
                                 {errors.amount && <Field.ErrorText>{errors.amount.message}</Field.ErrorText>}
                              </Field.Root>
                           </Stack>
                        </Dialog.Body>
                        <Dialog.Footer justifyContent="center" pt="0">
                           <Button
                              variant="outline"
                              h="10"
                              w="120px"
                              bg="darkerBtn"
                              color="white"
                              border="none"
                              textStyle="xs"
                              fontWeight="600"
                              onClick={onClose}
                           >
                              Cancel
                           </Button>

                           <Button
                              type="submit"
                              variant="outline"
                              h="10"
                              w="120px"
                              border="none"
                              textStyle="xs"
                              fontWeight="600"
                              disabled={isSubmitting}
                           >
                              Submit
                           </Button>
                        </Dialog.Footer>
                     </form>
                  </Dialog.Content>
               </Dialog.Positioner>
            </Portal>
         </Dialog.Root>
      </HStack>
   );
};

export default DiscountModalPackage;
